import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../generated/api';
import { trackUserJourney } from '../utils/datadog';
import { CREDIT_CARDS } from '../utils/constants';
import { getRumUser } from '../utils/users';

type AccountBalancesResponse = components['schemas']['AccountBalancesResponse'];
type AccountSummaryResponse = components['schemas']['AccountSummaryResponse'];
type TransactionResponse = components['schemas']['TransactionResponse'];
type CreditCardsResponse = components['schemas']['CreditCardsResponse'];
type RewardsBalanceResponse = components['schemas']['RewardsBalanceResponse'];

const AccountPage: React.FC = () => {
  const [accountBalances, setAccountBalances] = useState<AccountBalancesResponse | null>(null);
  const [accountSummary, setAccountSummary] = useState<AccountSummaryResponse | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<TransactionResponse | null>(null);
  const [creditCards, setCreditCards] = useState<CreditCardsResponse | null>(null);
  const [rewardsBalance, setRewardsBalance] = useState<RewardsBalanceResponse | null>(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    document.title = 'Bitsburg Bank - Account';

    // Gets the RUM user id that was previously set during login
    const username = getRumUser();
    const currentTimestamp = new Date().toISOString();

    trackUserJourney('account_page_visited', {
      previous_page: document.referrer || 'direct',
      timestamp: currentTimestamp,
    });

    // Check for JSON parse error occurrence
    if (Math.random() < 0.1) {
      try {
        window.DD_LOGS?.logger?.info('Checking for JSON parse error on account page');
        JSON.parse('invalid json string');
      } catch (error) {
        window.DD_LOGS?.logger?.error('JSON parse error on account page', { error: error });
        window.DD_RUM?.addError(error);
      }
    }

    // Load account data
    Promise.allSettled([
      fetch(`/api/account/balances?username=${encodeURIComponent(username)}`).then(
        (r) => r.json() as Promise<AccountBalancesResponse>
      ),
      fetch(`/api/account/summary?username=${encodeURIComponent(username)}&accountType=Checking`).then(
        (r) => r.json() as Promise<AccountSummaryResponse>
      ),
      fetch(`/api/account/transactions?username=${encodeURIComponent(username)}&limit=7`).then(
        (r) => r.json() as Promise<TransactionResponse>
      ),
      fetch(`/api/credit/cards?username=${encodeURIComponent(username)}`).then(
        (r) => r.json() as Promise<CreditCardsResponse>
      ),
      fetch(`/api/credit/rewards?username=${encodeURIComponent(username)}`).then(
        (r) => r.json() as Promise<RewardsBalanceResponse>
      ),
    ])
      .then(([balances, summary, transactions, cards, rewards]) => {
        if (balances.status === 'fulfilled') setAccountBalances(balances.value);
        if (summary.status === 'fulfilled') setAccountSummary(summary.value);
        if (transactions.status === 'fulfilled') setRecentTransactions(transactions.value);
        if (cards.status === 'fulfilled') {
          setCreditCards(cards.value);

          // Check credit card data
          window.DD_LOGS?.logger?.info(
            'Credit card data accessed on account page (Mastercard) ' + CREDIT_CARDS.MASTERCARD_FORMATTED
          );
          window.DD_RUM?.addAction('credit_card_data_accessed', {
            cardType: 'Mastercard',
            cardNumber: CREDIT_CARDS.MASTERCARD_FORMATTED,
          });
        }
        if (rewards.status === 'fulfilled') setRewardsBalance(rewards.value);
      })
      .catch((error: Error) => {
        window.DD_LOGS?.logger?.error('Error loading account data', { error: error });
        setErrorMessage('Failed to load account data');
        setShowErrorAlert(true);
      });
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatTransactionDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="py-4">
      <div className="container">
        {showErrorAlert && (
          <div className="row">
            <div className="col-12">
              <div className="alert bank-alert bank-alert-danger mb-4">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {errorMessage}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="row mb-4 mt-4">
          <div className="col-md-8">
            <div className="position-relative">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="bg-datadog-purple rounded-3 p-3 me-3"
                  style={{ boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}
                >
                  <i className="bi bi-people text-white fs-2"></i>
                </div>
                <div>
                  <h1 className="mb-1 fw-bold text-datadog-purple">My Accounts</h1>
                  <div className="d-flex align-items-center">
                    <div
                      className="bg-light rounded-pill px-3 py-1 border"
                      style={{ backgroundColor: 'rgba(99, 102, 241, 0.08)' }}
                    >
                      <i className="bi bi-clock text-datadog-purple me-2"></i>
                      <small className="text-muted fw-medium">
                        {accountSummary?.lastLoginDate
                          ? `Last login: ${formatDate(accountSummary.lastLoginDate)}`
                          : 'Welcome back'}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex justify-content-center justify-content-md-end">
              <div
                className="bank-card bank-card-no-hover"
                style={{
                  padding: '16px 20px',
                  background: 'rgba(99, 102, 241, 0.05)',
                  border: '1px solid rgba(99, 102, 241, 0.15)',
                  borderRadius: '12px',
                }}
              >
                <div className="text-center mb-3">
                  <span
                    className="badge bg-datadog-purple text-white px-3 py-1"
                    style={{ fontSize: '0.75rem', borderRadius: '20px' }}
                  >
                    <i className="bi bi-lightning me-1"></i>
                    Quick Actions
                  </span>
                </div>
                <div className="d-flex flex-column flex-lg-row gap-2 align-items-center">
                  <Link to="/billpay" className="btn bank-btn-outline" id="account-pay-bills-btn">
                    <i className="bi bi-credit-card me-2"></i>
                    Pay Bills
                  </Link>
                  <a className="btn bank-btn-outline" style={{ cursor: 'pointer' }} id="account-broken-link-btn">
                    <i className="bi bi-link-45deg me-2"></i>
                    Broken Link
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Balances */}
        {accountBalances && (
          <div className="row mb-4">
            <div className="col-md-4 mb-3">
              <div className="bank-card bank-card-elevated">
                <div className="bank-card-body text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <i className="bi bi-credit-card text-datadog-purple me-2"></i>
                    <h6 className="mb-0 text-muted">Checking Account</h6>
                  </div>
                  <h3 className="mb-1 fw-bold text-success">${accountBalances.checkingBalance}</h3>
                  <small className="text-muted">{accountBalances.checkingAccountNumber}</small>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="bank-card bank-card-elevated">
                <div className="bank-card-body text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <i className="bi bi-piggy-bank text-datadog-purple me-2"></i>
                    <h6 className="mb-0 text-muted">Savings Account</h6>
                  </div>
                  <h3 className="mb-1 fw-bold text-primary">${accountBalances.savingsBalance}</h3>
                  <small className="text-muted">{accountBalances.savingsAccountNumber}</small>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="bank-card bank-card-elevated">
                <div className="bank-card-body text-center">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <i className="bi bi-graph-up-arrow text-datadog-purple me-2"></i>
                    <h6 className="mb-0 text-muted">Investment Account</h6>
                  </div>
                  <h3 className="mb-1 fw-bold text-info">${accountBalances.investmentBalance}</h3>
                  <small className="text-muted">{accountBalances.investmentAccountNumber}</small>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          {/* Recent Transactions */}
          <div className="col-lg-8 mb-4">
            <div className="bank-card bank-card-elevated">
              <div className="bank-card-header">
                <h5 className="mb-0 fw-bold">
                  <i className="bi bi-clock-history me-2"></i>
                  Recent Transactions
                </h5>
              </div>
              <div className="bank-card-body">
                {recentTransactions?.transactions && recentTransactions.transactions.length > 0 ? (
                  recentTransactions.transactions.map((transaction, index) => (
                    <div
                      key={transaction.id}
                      className={`d-flex justify-content-between align-items-center ${index < (recentTransactions.transactions?.length ?? 0) - 1 ? 'border-bottom' : ''} py-3`}
                    >
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          {(transaction.amount ?? 0) > 0 ? (
                            <div className="bg-success bg-opacity-10 text-success rounded-circle p-2">
                              <i className="bi bi-arrow-up"></i>
                            </div>
                          ) : (
                            <div className="bg-danger bg-opacity-10 text-danger rounded-circle p-2">
                              <i className="bi bi-arrow-down"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="fw-semibold">{transaction.description}</div>
                          <small className="text-muted">{formatTransactionDate(transaction.date ?? '')}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className={`fw-bold ${(transaction.amount ?? 0) > 0 ? 'text-success' : 'text-danger'}`}>
                          {(transaction.amount ?? 0) > 0 ? '+' : '-'}${Math.abs(transaction.amount ?? 0)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <i className="bi bi-inbox display-4 text-muted"></i>
                    <p className="text-muted mt-2">No recent transactions</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Credit Cards & Rewards */}
          <div className="col-lg-4">
            {/* Credit Cards */}
            {creditCards?.cards && creditCards.cards.length > 0 && (
              <div className="bank-card bank-card-elevated mb-4">
                <div className="bank-card-header">
                  <h5 className="mb-0 fw-bold">
                    <i className="bi bi-credit-card-2-front me-2"></i>
                    Credit Cards
                  </h5>
                </div>
                <div className="bank-card-body">
                  {creditCards.cards.map((card, index) => (
                    <div
                      key={index}
                      className={`mb-3 ${(creditCards.cards?.length ?? 0) > 1 && index < (creditCards.cards?.length ?? 0) - 1 ? 'border-bottom pb-3' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="fw-semibold">{card.cardType}</div>
                          <small className="text-muted">{card.cardNumber}</small>
                        </div>
                        <span
                          className={`badge ${(card.utilization ?? 0) > 80 ? 'bg-danger' : (card.utilization ?? 0) > 50 ? 'bg-warning' : 'bg-success'}`}
                        >
                          {card.utilization ?? 0}%
                        </span>
                      </div>
                      <div className="progress mb-2" style={{ height: '4px' }}>
                        <div
                          className={`progress-bar ${(card.utilization ?? 0) > 80 ? 'bg-danger' : (card.utilization ?? 0) > 50 ? 'bg-warning' : 'bg-success'}`}
                          style={{ width: `${card.utilization ?? 0}%` }}
                        ></div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">Balance: ${card.balance ?? 0}</small>
                        <small className="text-muted">Limit: ${card.creditLimit ?? 0}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rewards */}
            {rewardsBalance && (
              <div className="bank-card bank-card-elevated">
                <div className="bank-card-header">
                  <h5 className="mb-0 fw-bold">
                    <i className="bi bi-gift me-2"></i>
                    Rewards
                  </h5>
                </div>
                <div className="bank-card-body pt-3">
                  <div className="text-center mb-3">
                    <h4 className="mb-1 fw-bold text-datadog-purple">${rewardsBalance.cashBackBalance}</h4>
                    <small className="text-muted">Cash Back Available</small>
                  </div>
                  <div className="text-center mb-3">
                    <h5 className="mb-1 fw-bold">{rewardsBalance.pointsBalance}</h5>
                    <small className="text-muted">Reward Points</small>
                  </div>
                  {(rewardsBalance.pointsToNextTier ?? 0) > 0 && (
                    <div className="text-center">
                      <small className="text-muted">
                        {rewardsBalance.pointsToNextTier ?? 0} points to {rewardsBalance.nextRewardTier}
                      </small>
                      <div className="progress mt-2" style={{ height: '4px' }}>
                        <div
                          className="progress-bar bg-datadog-purple"
                          style={{ width: `${Math.max(10, ((rewardsBalance.pointsBalance ?? 0) / 10000) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
